/**
 * API とマスタ JSON のレスポンス型（doc/api.md）。
 *
 * packages/* の型を直接引かないのは、フロントに採点ロジックを持ち込まないため。
 * 参照するのは「サーバが返してきた形」だけにしておく。
 */

/** 診断の深さ 3 つ（quick ⊂ standard ⊂ deep）。 */
export type DepthMode = 'quick' | 'standard' | 'deep';
/** 深さ 3 つに、別の質問紙 `bigfive`（性格 5 因子・60 問）を足したもの。 */
export type Mode = DepthMode | 'bigfive';

export interface Question {
  readonly id: string;
  readonly facet: string;
  readonly axis: string;
  readonly direction: string;
  readonly modes: readonly Mode[];
  readonly text: string;
  readonly order: number;
}

export interface Followup {
  readonly id: string;
  readonly axis: string;
  readonly optionA: { readonly text: string };
  readonly optionB: { readonly text: string };
}

export interface QuestionSet {
  readonly version: number;
  readonly scale: { readonly labels: readonly string[] };
  /** 主軸の questions.json は深さ 3 つ、bigfive.json から組んだものは bigfive だけを持つ。 */
  readonly modes: Partial<Record<Mode, { readonly label: string; readonly questionIds: readonly string[] }>>;
  readonly questions: readonly Question[];
  readonly followups: readonly Followup[];
}

// ---------------------------------------------------------------- ビッグファイブ版（bigfive.json。doc/ASSESSMENT.md §9）

export type BigFiveDomain = 'E' | 'A' | 'C' | 'N' | 'O';

export interface BigFiveDomainMeta {
  readonly id: BigFiveDomain;
  readonly name: string;
  readonly en: string;
  readonly measures: string;
  readonly high: string;
  readonly low: string;
  readonly facets: readonly string[];
}

export interface BigFiveFacetMeta {
  readonly id: string;
  readonly domain: BigFiveDomain;
  readonly name: string;
  readonly en: string;
  readonly high: string;
  readonly low: string;
}

export interface BigFiveQuestion {
  readonly id: string;
  readonly domain: BigFiveDomain;
  readonly facet: string;
  readonly keyed: 1 | -1;
  readonly text: string;
  readonly order: number;
}

/** 静的 JSON `bigfive.json`。因子・側面の名前と説明文の正はここ（画面は二重に持たない）。 */
export interface BigFiveSet {
  readonly instrument: 'bigfive';
  readonly version: number;
  readonly title: string;
  readonly description: string;
  readonly structure: string;
  readonly scale: { readonly labels: readonly string[] };
  readonly domains: readonly BigFiveDomainMeta[];
  readonly facets: readonly BigFiveFacetMeta[];
  readonly questionIds: readonly string[];
  readonly questions: readonly BigFiveQuestion[];
}

/** 結果に付くビッグファイブ版の主結果。スコアは 0〜100（基準があれば百分位、無ければ素点）。SEM は帯の幅。 */
export interface BigFiveResult {
  readonly version: number;
  readonly domains: Record<BigFiveDomain, number>;
  readonly facets: Record<string, number>;
  readonly domainSem: Record<BigFiveDomain, number>;
  readonly facetSem: Record<string, number>;
  readonly normVersion: number;
}

export type Certainty = 'historical' | 'traditional' | 'legend' | 'generated';

/** キャラの要約。絵がまだ無いキャラは portrait: null で、絵文字で描く。 */
export interface CharacterBrief {
  readonly id: string;
  readonly name: string;
  readonly epithet: string;
  readonly label: string;
  readonly portrait: string | null;
  readonly emoji: string | null;
}

/** 結果が属するテーマ。見出し・シェア文・「もう一度」の戻り先に使う。 */
export interface ThemeRef {
  readonly themeId: string;
  readonly title: string;
  readonly subject: string;
  /** 型の粒度（4・8・16・32・64）。古い結果には無い。 */
  readonly typeCount?: number;
  readonly href: string;
}

export interface Reason {
  readonly facet: string;
  readonly facetLabel: string;
  readonly userTrait: string | null;
  readonly headline: string;
  readonly body: string;
  readonly certainty: Certainty;
  readonly text: string;
}

export interface DiagnosisResult {
  readonly resultId: string;
  readonly theme: ThemeRef;
  readonly mode: Mode;
  readonly createdAt: string;
  readonly character: CharacterBrief & {
    readonly summary: string;
  };
  readonly match: {
    readonly similarity: number;
    readonly runnerUp: (CharacterBrief & { readonly similarity: number }) | null;
    readonly margin: number;
    readonly close: boolean;
  };
  /** 測定から出た型コード（deep なら 6 軸）。画面の見出しには displayType を使う。 */
  readonly type: string;
  /** 画面に主として出す型。測定コードをそのテーマの粒度に縮めたもの。古い結果には無い。 */
  readonly displayType?: string;
  /** いちばん近いキャラの型（同じ粒度）。向きで選ぶので displayType と一致しないことがある。 */
  readonly characterType?: string;
  readonly type16: string;
  readonly identity: string | null;
  readonly axisScores: Record<string, number>;
  readonly facetScores: Record<string, number>;
  readonly measuredFacets: readonly string[];
  readonly contestedAxes: readonly string[];
  /** 軸ごとの「その極である確からしさ」0.5〜1.0。古い結果には無い。 */
  readonly axisCertainty: Record<string, number> | null;
  /** ビッグファイブ版（mode が bigfive）のときだけ。このとき type・axisScores・facetScores は 5 因子から写した目安。 */
  readonly bigFive?: BigFiveResult | null;
  /** 再受検なら前回の要約（安定性の確認）。 */
  readonly retest: {
    readonly resultId: string;
    readonly createdAt: string;
    readonly type: string;
    readonly characterId: string;
    readonly characterName: string | null;
    readonly axisScores: Record<string, number>;
    /** 前回がビッグファイブ版なら 5 因子。古い結果には無い。 */
    readonly bigFiveDomains?: Record<string, number> | null;
    readonly daysBetween: number;
  } | null;
  /** 併存尺度に答え済みか。 */
  readonly validated: boolean;
  readonly confidence: number;
  readonly normVersion: number;
  readonly questionSetVersion: number;
  readonly reasons: readonly Reason[];
  readonly partner: (CharacterBrief & { readonly reason: string }) | null;
  readonly rival: CharacterBrief | null;
  readonly shareUrl: string;
  readonly disclaimer: string;
}

export interface SubmitResponse {
  readonly resultId?: string;
  readonly needsFollowup: boolean;
  readonly followups: readonly Followup[];
  readonly contestedAxes?: readonly string[];
  readonly result?: DiagnosisResult;
}

// ---------------------------------------------------------------- 相性と付き合い方（`GET /v1/compat`。doc/ASSESSMENT.md §10）

export interface TypePairAxis {
  readonly axis: string;
  readonly caption: string;
  readonly area: string;
  readonly relation: 'same' | 'different';
  readonly you: { readonly code: string; readonly name: string };
  readonly partner: { readonly code: string; readonly name: string };
  readonly works: string;
  readonly friction: string;
  readonly tips: readonly string[];
}

export interface TypePairGuide {
  readonly you: string;
  readonly partner: string;
  readonly sameAxes: number;
  readonly summary: string;
  readonly axes: readonly TypePairAxis[];
  readonly note: string;
}

export interface CompatItem {
  readonly facet: string;
  readonly label: string;
  readonly axis: string;
  readonly kind: 'gap' | 'shared';
  readonly you: number;
  readonly partner: number;
  readonly diff: number;
  readonly youTrait: string;
  readonly partnerTrait: string;
  readonly advice: string;
}

export interface CompatArea {
  readonly axis: string;
  readonly caption: string;
  readonly area: string;
  readonly you: number | null;
  readonly partner: number | null;
  readonly facets: readonly { readonly facet: string; readonly label: string; readonly you: number; readonly partner: number }[];
  readonly items: readonly CompatItem[];
}

export interface CompatSideBrief {
  readonly resultId: string;
  readonly href: string;
  readonly theme: { readonly themeId: string; readonly title: string; readonly href: string };
  readonly character: CharacterBrief | null;
  readonly displayType: string;
  readonly type16: string;
  readonly mode: Mode;
  readonly normVersion: number;
  readonly createdAt: string;
}

export interface CompatResponse {
  readonly you: CompatSideBrief;
  readonly partner: CompatSideBrief;
  readonly compat: {
    readonly measured: number;
    readonly gapCount: number;
    readonly sharedCount: number;
    readonly summary: string;
    readonly focus: readonly CompatItem[];
    readonly common: readonly CompatItem[];
    readonly areas: readonly CompatArea[];
    readonly thresholds: { readonly gap: number; readonly lean: number };
  };
  readonly mapped: boolean;
}

export interface CompatRead {
  readonly overview: string;
  readonly points: readonly { readonly title: string; readonly body: string }[];
  readonly avoid: readonly string[];
}

export interface CompatAiQuota {
  readonly limit: number | null;
  readonly used: number;
  readonly remaining: number | null;
  readonly resetsAt: string;
}

/** エピソードの絵（AI の読みの points と同じ並び。描けていないところは null）。パスは `compat/{key}/scene-{i}.jpg`。 */
export interface CompatScenes {
  readonly status: 'running' | 'done' | 'partial';
  readonly images: readonly (string | null)[];
  readonly updatedAt: string;
}

/** `GET /v1/compat/ai`。read は読み済みのときだけ。relations は選べる関係（キー → 表示名）。 */
export interface CompatAiStatus {
  readonly relation: string | null;
  readonly read: CompatRead | null;
  readonly model: string | null;
  readonly quota: CompatAiQuota;
  readonly relations: Record<string, string>;
  /** エピソードの絵。頼まれるまで null。 */
  readonly scenes: CompatScenes | null;
  /** エピソードの絵を頼める回数（読みとは別勘定）。 */
  readonly sceneQuota: CompatAiQuota;
  /** この環境で絵を描けるか。 */
  readonly scenesAvailable: boolean;
}

/** `POST /v1/compat/scenes`。 */
export interface CompatScenesResponse {
  readonly scenes: CompatScenes;
  readonly started: boolean;
  readonly sceneQuota: CompatAiQuota;
}

export interface CompatAiResponse {
  readonly relation: string;
  readonly relationLabel: string;
  readonly read: CompatRead;
  readonly model: string;
  readonly cached: boolean;
  readonly quota: CompatAiQuota;
  readonly scenes: CompatScenes | null;
}

// ---------------------------------------------------------------- 結果ページ（`GET /r/:resultId`）

export interface ResultPageResponse {
  readonly result: {
    readonly resultId: string;
    readonly themeId: string;
    readonly typeCode: string;
    readonly characterId: string;
    readonly facetProfile: Record<string, number>;
  };
  readonly view: DiagnosisResult;
  /** この結果ページに広告を出してよいか（その診断の受けた回数・通報。doc/ADS.md §5）。 */
  readonly adsAllowed: boolean;
}

// ---------------------------------------------------------------- 診断テーマ（プロンプトから生成）

/** `POST /v1/themes` の応答。 */
export interface CreateThemeResponse {
  readonly themeId: string;
  readonly title: string;
  readonly subject: string;
  readonly characterCount: number;
  /** 実際のキャラ（タイプ）数。4・8・16・32。 */
  readonly typeCount: number;
  /** 頼んだ数。分けられる数に丸めたときは typeCount と違う。 */
  readonly requestedCount: number;
  readonly generatedBy: string;
  /** Gemini が使えず、保険のフィクスチャで作ったとき true。 */
  readonly fallback: boolean;
  readonly images: ThemeImages | null;
  /** ログインせずに作ったときだけ返る合言葉。あとで引き取って絵を付けるのに使う。 */
  readonly creatorToken: string | null;
  /** ログイン済みのときだけ。今日あと何回 絵を描けるか。 */
  readonly imageQuota: ImageQuota | null;
}

/**
 * キャラ画像の進み具合。テーマ作成後に裏で描く。pending / running のあいだ入口ページはポーリングする。
 * login_required はログインせずに作ったとき、quota はその日の上限に達していたとき（doc/AUTH.md §1）。
 */
export interface ThemeImages {
  readonly status: 'pending' | 'running' | 'done' | 'partial' | 'skipped' | 'login_required' | 'quota';
  readonly model: string | null;
  readonly done: number;
  readonly total: number;
  readonly updatedAt: string;
}

/** 画像生成の残り回数（doc/AUTH.md §1）。管理者は limit・remaining が null（数えない）。 */
export interface ImageQuota {
  readonly limit: number | null;
  readonly used: number;
  readonly remaining: number | null;
  /** 次に戻る瞬間（日本時間の翌 0 時）。 */
  readonly resetsAt: string;
}

/** `GET /v1/themes` の 1 件。トップの「人気の診断」カード。 */
export interface ThemeSummary {
  readonly themeId: string;
  readonly title: string;
  readonly subject: string;
  readonly createdAt: string | null;
  readonly plays: number | null;
  readonly characterCount: number;
  readonly sample: readonly { readonly name: string; readonly emoji: string | null; readonly portrait: string | null }[];
  readonly href: string;
}

export interface ThemeCharacter {
  readonly id: string;
  readonly name: string;
  readonly epithet: string;
  readonly label: string;
  readonly type: string;
  readonly emoji: string | null;
  readonly portrait: string | null;
}

/** ログイン中の人が、その診断に対してできること（doc/AUTH.md §2）。未ログインなら null。 */
export interface ThemeViewer {
  readonly uid: string;
  readonly isAdmin: boolean;
  readonly isOwner: boolean;
  readonly canDelete: boolean;
  readonly canDrawImages: boolean;
  readonly imageQuota: ImageQuota;
}

/** `GET /v1/themes/:themeId`。診断の入口ページ。 */
export interface ThemeView extends ThemeSummary {
  /** 生成した診断か、運営が取り込んだ診断か。 */
  readonly origin: 'generated' | 'imported';
  readonly intro: string;
  /** 型の粒度（4・8・16・32・64）。 */
  readonly typeCount: number;
  /** 取り込んだ診断は null。 */
  readonly prompt: string | null;
  readonly generatedBy: string;
  /** ログインの仕組みより前に作られた古い診断は null。 */
  readonly images: ThemeImages | null;
  /** ログインせずに作られた診断（誰でも消せる）。 */
  readonly ownerless: boolean;
  /** この入口に広告を出してよいか（受けた回数・通報。doc/ADS.md §5）。 */
  readonly adsAllowed: boolean;
  readonly viewer: ThemeViewer | null;
  readonly characters: readonly ThemeCharacter[];
}

export interface ThemeImagesResponse {
  readonly themeId: string;
  readonly started: boolean;
  readonly claimed: boolean;
  readonly enabled: boolean;
  readonly images: ThemeImages | null;
  readonly imageQuota: ImageQuota;
}

// ---------------------------------------------------------------- ログイン本人と管理メニュー（doc/AUTH.md）

export interface MeResponse {
  readonly uid: string;
  readonly isAdmin: boolean;
  /** どのログイン方法で入っているか（`google.com` `password` など）。管理者になれるのは Google だけ。 */
  readonly provider: string | null;
  readonly displayName: string | null;
  readonly email: string | null;
  readonly emailVerified: boolean;
  readonly imageQuota: ImageQuota;
  /** 利用規約・プライバシーポリシーへの同意（doc/AUTH.md §13）。 */
  readonly terms: TermsStatus;
}

/** 同意の記録。何の版に、いつ同意したか。 */
export interface ConsentRecord {
  readonly version: string;
  readonly privacyVersion?: string;
  readonly agreedAt: string;
}

/**
 * `GET /v1/me` の `terms`。`current` が false なら、いまの版に同意していない（初めてか、版が上がった）。
 * 画面はこれを見てもう一度確かめる。門はサーバ側（services/api/src/lib/terms.ts）で、画面の出し分けは飾り。
 */
export interface TermsStatus {
  readonly current: boolean;
  readonly agreed: ConsentRecord | null;
  readonly required: { readonly version: string; readonly privacyVersion: string };
}

/** サイト全体の 1 日の使用状況（`GET /v1/admin/limits`）。 */
export interface SiteQuota {
  /** 上限。null は上限なし。 */
  readonly limit: number | null;
  readonly used: number;
  readonly remaining: number | null;
  readonly resetsAt: string;
}

/** サイト全体の生成数の上限（doc/AUTH.md §15）。管理メニューで変える。 */
/** 診断 1 件あたりの料金（ドル）。 */
export interface CostRow {
  readonly characters: number;
  readonly textOnly: number;
  readonly withImages: number;
}

/** 料金の見積もり（services/api/src/lib/costs.ts）。請求額ではない。 */
export interface CostEstimate {
  readonly usdJpy: number;
  readonly prices: {
    readonly textModel: string;
    readonly textInputPerM: number;
    readonly textOutputPerM: number;
    readonly textPriceFrom: string;
    readonly nextTextPrice: { readonly from: string; readonly inputPerM: number; readonly outputPerM: number } | null;
    readonly imageModel: string;
    readonly imagePerImage: number;
  };
  readonly perTheme: readonly CostRow[];
  /** 上限まで使ったときの 1 日の最大（ドル）。サイト全体の上限が無ければ null。 */
  readonly dailyMax: { readonly typical: number | null; readonly largest: number | null };
  readonly today: { readonly themes: number; readonly imagesDrawn: number; readonly usd: number };
}

export interface AdminLimits {
  readonly themeDailyLimit: number | null;
  readonly imageDailyLimit: number | null;
  /** 1 人あたり。ログインしていない人の診断（0 でログイン必須）。 */
  readonly anonThemeDailyLimit: number;
  /** 1 人あたり。ログインした人の診断。null は上限なし。 */
  readonly userThemeDailyLimit: number | null;
  /** 1 人あたり。ログインした人の画像つき診断。null は上限なし。 */
  readonly userImageDailyLimit: number | null;
  /** サイト全体。1 日に作れる動画の本数（管理者だけが作る）。null は上限なし。 */
  readonly videoDailyLimit: number | null;
  /** サイト全体。1 日に書ける相性の AI の読み。null は上限なし。 */
  readonly compatDailyLimit: number | null;
  /** 1 人あたり。ログインした人の相性の AI の読み。null は上限なし。 */
  readonly userCompatDailyLimit: number | null;
  /** サイト全体。1 日に描ける相性のエピソードの絵の回数（1 回で 3 枚）。null は上限なし。 */
  readonly compatImageDailyLimit: number | null;
  /** 1 人あたり。ログインした人が頼める相性のエピソードの絵。null は上限なし。 */
  readonly userCompatImageDailyLimit: number | null;
  /** `env` は環境変数の既定値のまま、`settings` は管理メニューで置いた値。 */
  readonly source: 'env' | 'settings';
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
  readonly today: string;
  readonly usage: { readonly theme: SiteQuota; readonly image: SiteQuota; readonly video: SiteQuota; readonly compat: SiteQuota; readonly compatImage: SiteQuota };
  readonly cost: CostEstimate;
}

/** 問い合わせの種類（`/contact` のフォーム）。 */
export type ContactCategory = 'result' | 'theme' | 'account' | 'ads' | 'delete' | 'other';

/** 自分が送った問い合わせ（`GET /v1/me/contacts`）。 */
export interface MyContact {
  readonly contactId: string;
  readonly category: ContactCategory;
  readonly message: string;
  readonly resultId: string | null;
  readonly status: 'open' | 'done';
  readonly createdAt: string;
  readonly handledAt: string | null;
}

/** 管理メニューの問い合わせ（`GET /v1/admin/contacts`）。 */
export interface AdminContact {
  readonly contactId: string;
  readonly category: ContactCategory;
  readonly message: string;
  readonly resultId: string | null;
  /** 返信先。本人が書いていて、まだ対応が済んでいないときだけ入る。 */
  readonly replyTo: string | null;
  readonly status: 'open' | 'done';
  readonly createdAt: string;
  readonly handledAt: string | null;
  readonly from: string;
  readonly href: string | null;
}

/** `GET /v1/policies`。本文の頭に出す版と最終更新日。ログイン不要。 */
export interface PolicyVersions {
  readonly terms: { readonly version: string; readonly updatedAt: string; readonly href: string };
  readonly privacy: { readonly version: string; readonly updatedAt: string; readonly href: string };
}

/** `/me` の「受けた診断」。ログインして受けたものだけ並ぶ。 */
export interface MyResult {
  readonly resultId: string;
  readonly createdAt: string;
  readonly mode: Mode;
  readonly typeCode: string;
  readonly themeId: string;
  readonly themeTitle: string;
  readonly themeDeleted: boolean;
  readonly characterName: string | null;
  readonly characterEmoji: string | null;
  readonly characterPortrait: string | null;
  readonly confidence: number;
  readonly href: string;
}

/**
 * 使い回せる過去の回答（`GET /v1/me/answers`）。ログインして受けた結果のうち、いまの設問版で回答が保存されているもの。
 * 回答そのものは来ない。`missing` は深さごとに「まだ答えていない設問の数」。
 */
export interface ReusableAnswers {
  readonly resultId: string;
  readonly createdAt: string;
  readonly mode: Mode;
  readonly themeId: string;
  readonly themeTitle: string;
  readonly characterName: string | null;
  readonly answered: number;
  readonly missing: Record<Mode, number>;
}

/** `POST /v1/sessions` の応答。`reused` は過去の回答を写して始めたときだけ。 */
export interface SessionCreated {
  readonly sessionId: string;
  readonly mode: Mode;
  readonly expiresAt: string;
  readonly reused: {
    readonly resultId: string;
    readonly answered: number;
    readonly missing: number;
    /** まだ答えていない設問の ID（設問の並び順）。画面はこれだけ出す。 */
    readonly missingIds: readonly string[];
  } | null;
}

/** `/me` に並べる、自分が作った診断。 */
export interface MyTheme {
  readonly themeId: string;
  readonly title: string;
  readonly subject: string;
  readonly createdAt: string;
  readonly plays: number;
  readonly characterCount: number;
  readonly images: ThemeImages | null;
  readonly deletedAt: string | null;
  readonly href: string;
}

export interface AdminTheme extends MyTheme {
  readonly prompt: string;
  readonly generatedBy: string;
  readonly typeCount: number;
  readonly createdBy: 'user' | 'anonymous' | 'import';
  /** 作った人の uid の先頭だけ。 */
  readonly owner: string | null;
  readonly deletedBy: string | null;
}

export interface AdminAccount {
  readonly uid: string;
  /** Firebase から引いて出すだけ（保存していない）。引けなければ null。 */
  readonly email: string | null;
  readonly name: string | null;
  readonly role: 'user' | 'admin';
  /** env = 環境変数 ADMIN_EMAILS と一致、manual = 管理メニューから手で付けた。 */
  readonly roleSource: 'env' | 'manual' | null;
  readonly imageLimitOverride: number | null;
  readonly usedToday: number;
  readonly createdAt: string;
  readonly lastSeenAt: string;
}

export interface AdminStats {
  readonly today: string;
  readonly imageRuns: { readonly today: number; readonly month: number };
  readonly imagesDrawn: { readonly today: number; readonly month: number };
  readonly themesCreated: { readonly today: number; readonly month: number };
  readonly themesDeleted: { readonly today: number; readonly month: number };
  readonly diagnoses: { readonly today: number; readonly month: number };
  readonly days: readonly { readonly day: string; readonly imageRuns: number; readonly imagesDrawn: number; readonly themesCreated: number }[];
}

export interface AdminResult {
  readonly resultId: string;
  readonly themeId: string;
  readonly typeCode: string;
  readonly characterId: string;
  readonly mode: Mode;
  readonly createdAt: string;
  /** ログインして受けたか。 */
  readonly linked: boolean;
  readonly href: string;
}

export interface AdminLog {
  readonly action: string;
  readonly actor: string;
  readonly targetId: string;
  readonly at: string;
  readonly note: string | null;
}

// ---------------------------------------------------------------- このサイトの設定（doc/ADS.md）

/** 広告を出す場所。services/api の `AdPlacement` と揃える。診断の途中（設問の画面）には出さない。 */
export type AdPlacement = 'home' | 'landing' | 'result' | 'help';

export const AD_PLACEMENTS: readonly { readonly id: AdPlacement; readonly label: string }[] = [
  { id: 'home', label: 'トップ' },
  { id: 'landing', label: '診断の入口' },
  { id: 'result', label: '結果ページ' },
  { id: 'help', label: 'ヘルプ・読み物' },
];

/** アフィリエイトの ASP。表示と集計の目印だけ（飛び先の形はどれも同じに扱う）。 */
export const AFFILIATE_NETWORKS: readonly { readonly id: string; readonly label: string }[] = [
  { id: 'a8', label: 'A8.net' },
  { id: 'moshimo', label: 'もしもアフィリエイト' },
  { id: 'valuecommerce', label: 'バリューコマース' },
  { id: 'amazon', label: 'Amazon アソシエイト' },
  { id: 'rakuten', label: '楽天アフィリエイト' },
  { id: 'afb', label: 'afb' },
  { id: 'other', label: 'そのほか' },
];

export interface PublicAffiliate {
  readonly id: string;
  readonly network: string;
  readonly placements: readonly AdPlacement[];
  readonly title: string;
  readonly text: string;
  readonly url: string;
  readonly imageUrl: string | null;
  readonly pixelUrl: string | null;
}

export interface PublicAds {
  readonly adsense: { readonly client: string; readonly slots: Partial<Record<AdPlacement, string>> } | null;
  readonly affiliate: readonly PublicAffiliate[];
}

/** `GET /v1/config`（本番は index.html の `window.__GENDAN_CONFIG__`）。 */
export interface SiteConfig {
  readonly features: { readonly ads: boolean };
  readonly ads: PublicAds | null;
  /** 1 人あたりの回数（管理メニューの「上限」で変わる）。古いサーバは返さない。 */
  readonly limits?: SiteLimitsPublic;
  /** App Check（reCAPTCHA Enterprise）のサイトキー。使わないサイトでは null（doc/AUTH.md §22）。 */
  readonly appCheck?: { readonly siteKey: string } | null;
  /** Google Analytics の測定 ID。使わないサイトでは null（doc/ADS.md §6）。 */
  readonly analytics?: { readonly measurementId: string } | null;
}

/** `GET /v1/config` の `limits`（services/api の handlers/config.ts）。 */
export interface SiteLimitsPublic {
  /** ログインしていない人が 1 日に作れる診断。0 はログイン必須。 */
  readonly anonThemeDailyLimit: number;
  /** ログインした人が 1 日に絵を付けられる回数。null は上限なし。 */
  readonly userImageDailyLimit: number | null;
  /** 問い合わせの 1 人 1 日の通数。 */
  readonly contactDailyLimit: number;
  /** ログインした人が 1 日に頼める相性の AI の読み。null は上限なし。古いサーバには無い。 */
  readonly userCompatDailyLimit?: number | null;
  /** ログインした人が 1 日に頼める相性のエピソードの絵（1 回 3 枚）。null は上限なし。古いサーバには無い。 */
  readonly userCompatImageDailyLimit?: number | null;
}

export interface AffiliateItem extends PublicAffiliate {
  readonly enabled: boolean;
}

/** `GET /v1/admin/ads`。 */
export interface AdminAds {
  readonly featureEnabled: boolean;
  readonly adsenseConfig: PublicAds['adsense'];
  readonly adsense: boolean;
  readonly affiliate: boolean;
  readonly items: readonly AffiliateItem[];
  /** 利用者が作った診断の入口・結果に広告を出すのに要る「受けた回数」。 */
  readonly minPlays: number;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
}

/** 診断の通報の理由（doc/ADS.md §5）。 */
export type ReportReason = 'hate' | 'sexual' | 'person' | 'violence' | 'spam' | 'other';

export const REPORT_REASONS: readonly { readonly id: ReportReason; readonly label: string }[] = [
  { id: 'hate', label: '差別的・人を傷つける' },
  { id: 'sexual', label: 'わいせつ' },
  { id: 'person', label: '実在の人を特定できる' },
  { id: 'violence', label: '暴力・犯罪をあおる' },
  { id: 'spam', label: '宣伝・スパム' },
  { id: 'other', label: 'その他' },
];

/** `GET /v1/admin/reports`。診断ごとにまとめた通報。 */
export interface AdminReportGroup {
  readonly themeId: string;
  readonly title: string;
  readonly createdBy: 'user' | 'anonymous' | 'import' | null;
  readonly plays: number;
  readonly deletedAt: string | null;
  readonly openReports: number;
  readonly adsAllowed: boolean;
  readonly href: string;
  readonly reports: readonly {
    readonly reportId: string;
    readonly reason: ReportReason;
    readonly note: string;
    readonly status: 'open' | 'done';
    readonly createdAt: string;
    readonly href: string | null;
  }[];
}

// ---------------------------------------------------------------- 相性ドラマの動画（管理者だけ。services/api/src/lib/video.ts）

export type VideoStatus = 'queued' | 'planning' | 'generating' | 'stitching' | 'done' | 'failed';

export interface AdminVideo {
  readonly videoId: string;
  readonly themeId: string;
  readonly themeTitle: string;
  /** themeId・themeTitle はキャラごとの診断（古い動画には無い）。 */
  readonly characters: readonly {
    readonly id: string; readonly name: string; readonly epithet: string; readonly label: string; readonly type: string; readonly emoji: string; readonly portrait: string | null;
    readonly themeId?: string; readonly themeTitle?: string;
  }[];
  readonly prompt: string;
  readonly durationSec: number;
  readonly sceneSec?: number;
  readonly aspectRatio: '9:16' | '16:9';
  readonly resolution: '360p' | '720p' | '1080p';
  readonly captions: boolean;
  readonly voice?: boolean;
  readonly continuity?: 'scene' | 'frame';
  readonly status: VideoStatus;
  readonly storyboard: {
    readonly title: string;
    readonly logline: string;
    readonly chemistry: string;
    readonly cast: readonly { readonly id: string; readonly callName?: string; readonly voice?: string }[];
    readonly scenes: readonly {
      readonly seconds: number;
      readonly visual: string;
      readonly caption: string;
      readonly lines?: readonly { readonly speaker: string; readonly text: string; readonly kind?: 'line' | 'reaction'; readonly start: number; readonly end: number }[];
      readonly sfx?: string;
    }[];
  } | null;
  readonly done: number;
  readonly total: number;
  readonly url: string | null;
  readonly outputSec: number | null;
  readonly model: string;
  readonly error: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly estimateUsd: number;
  /** カットごとの案（最大 maxTakes 本）。selected が使っている案。 */
  readonly cuts?: readonly {
    readonly index: number;
    readonly selected: number;
    readonly takes: readonly { readonly url: string; readonly seconds: number; readonly mismatch?: number | null; readonly linesOk?: boolean | null; readonly sameVoice?: number | null; readonly heard?: string | null; readonly screenText?: string | null }[];
  }[];
  readonly maxTakes?: number;
  readonly maxTakesPerRun?: number;
  /** 再生成を待っているカット。 */
  readonly regen?: readonly number[] | null;
}

export interface AdminVideos {
  readonly videos: readonly AdminVideo[];
  readonly quota: SiteQuota;
  readonly model: string;
  readonly pricePerSecond: number;
  readonly limits: {
    readonly minSec: number; readonly maxSec: number; readonly extendMaxSec: number;
    readonly sceneSec: number; readonly sceneSecChoices: readonly number[]; readonly defaultScenes: number; readonly minScenes: number; readonly maxScenes: number;
  };
}

export interface VideoChemistry {
  readonly same: readonly string[];
  readonly different: readonly string[];
  readonly traits: Readonly<Record<string, readonly string[]>>;
}

/** 動画に出す 1 人（どの診断のどのキャラか）。2 人は違う診断でもよい。 */
export interface CastRef {
  readonly themeId: string;
  readonly characterId: string;
}

export interface NewVideo {
  readonly cast: readonly [CastRef, CastRef];
  readonly prompt: string;
  readonly sceneCount: number;
  readonly sceneSec: number;
  readonly aspectRatio: '9:16' | '16:9';
  readonly resolution: '360p' | '720p' | '1080p';
  readonly captions: boolean;
  readonly voice: boolean;
  readonly continuity: 'scene' | 'frame';
}
